import React, { useState, useEffect } from "react";
import config from "../config";
import "./UserSelector.css";

const UserSelector = ({
  selectedUsers = [],
  onChange,
  onShare,
  onClose,
  shareDisabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [tpUsers, setTpUsers] = useState([]);
  const [userNameMap, setUserNameMap] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch TP users when component mounts
  useEffect(() => {
    const fetchTpUsers = async () => {
      setIsLoadingUsers(true);
      setErrorMessage("");
      try {
        const response = await fetch(
          `${config.api.baseUrl}/dashboard/active-tp-users`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (data.success) {
          setTpUsers(data.data || []);

          // Create a mapping of usernames to full names
          const nameMap = {};
          data.data.forEach((user) => {
            const username = user.email.split("@")[0];
            nameMap[username] = `${user.firstName} ${user.lastName}`;
          });
          setUserNameMap(nameMap);
        } else {
          throw new Error(data.error || "Failed to fetch users");
        }
      } catch (err) {
        setErrorMessage("Error loading users: " + err.message);
        console.error("Error fetching TP users:", err);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchTpUsers();
  }, []);

  // Handle user search
  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query.length > 1) {
      setIsSearching(true);
      // Search local users in parallel to TP users
      Promise.all([
        Promise.resolve(
          tpUsers.filter(
            (user) =>
              user.email?.toLowerCase().includes(query) ||
              user.firstName?.toLowerCase().includes(query) ||
              user.lastName?.toLowerCase().includes(query)
          )
        ),
        fetch(
          `${config.api.baseUrl}/auth/users/local-search?q=${encodeURIComponent(
            query
          )}`,
          {
            credentials: "include",
          }
        )
          .then((r) => r.json())
          .then((d) =>
            d && d.success && Array.isArray(d.users) ? d.users : []
          )
          .catch(() => []),
      ]).then(([tp, local]) => {
        const merged = [...tp, ...local]
          .filter(
            (u, idx, arr) =>
              idx ===
              arr.findIndex(
                (x) =>
                  x.email &&
                  x.email.toLowerCase() === (u.email || "").toLowerCase()
              )
          )
          .slice(0, 8);
        setSearchResults(merged);
      });
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  // Handle selecting a user from search results
  const handleSelectUser = (e, user) => {
    e.preventDefault();
    e.stopPropagation();

    // Extract username from email (before @)
    const username = user.email.split("@")[0];

    // Update the user name map if not already there
    if (!userNameMap[username]) {
      setUserNameMap((prev) => ({
        ...prev,
        [username]: `${user.firstName} ${user.lastName}`,
      }));
    }

    // Check if user is already selected
    if (!selectedUsers.find((u) => u.email === user.email)) {
      onChange([...selectedUsers, user]);
    }

    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
  };

  // Handle removing a selected user
  const handleRemoveUser = (e, email) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(selectedUsers.filter((u) => u.email !== email));
  };

  // Get display name for a user
  const getDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email || "";
  };

  return (
    <div className="user-selector" onClick={(e) => e.stopPropagation()}>
      <div className="user-selector-header">
        <h3>Share with users</h3>
      </div>
      <div className="user-selector-content">
        <p>Search for users by name or email</p>
        <div className="user-search-container">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Start typing a name or email..."
            className="user-search-input"
          />

          {isLoadingUsers && (
            <div className="loading-message">Loading users...</div>
          )}
          {errorMessage && <div className="error-message">{errorMessage}</div>}

          {isSearching && searchResults.length > 0 && (
            <ul className="user-search-results">
              {searchResults.map((user) => (
                <li
                  key={user.email}
                  onClick={(e) => handleSelectUser(e, user)}
                  className="user-search-result"
                >
                  {user.avatarUri ? (
                    <img
                      src={`${config.api.baseUrl}${
                        config.api.endpoints.dashboard.tpAvatar
                      }?url=${encodeURIComponent(user.avatarUri)}`}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="user-avatar"
                    />
                  ) : (
                    <span
                      className="user-avatar-placeholder"
                      dangerouslySetInnerHTML={{
                        __html: `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 40 40'>
  <circle cx='20' cy='20' r='20' fill='#3469ff'/>
  <text x='50%' y='50%' fill='#ffffff' font-family='Roboto, sans-serif' font-size='14' font-weight='600' text-anchor='middle' dominant-baseline='central'>${`${(
    user.firstName || "?"
  )
    .charAt(0)
    .toUpperCase()}${(user.lastName || "?").charAt(0).toUpperCase()}`}</text>
</svg>`,
                      }}
                    />
                  )}
                  <div className="user-info">
                    <div className="user-name">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="user-email">{user.email}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {isSearching &&
            searchResults.length === 0 &&
            searchQuery.length > 1 && (
              <div className="no-results">No matching users found</div>
            )}
        </div>
      </div>

      {selectedUsers.length > 0 && (
        <div className="selected-users">
          <div className="selected-users-label">Selected users:</div>
          <div className="user-tags">
            {selectedUsers.map((user) => (
              <div key={user.email} className="user-tag">
                <span className="user-tag-text">{getDisplayName(user)}</span>
                <button
                  type="button"
                  className="user-tag-remove"
                  onClick={(e) => handleRemoveUser(e, user.email)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="user-selector-actions">
        <button
          className="share-cancel-btn"
          onClick={() => {
            if (onClose) onClose();
          }}
        >
          Cancel
        </button>
        <button
          className="share-submit-btn"
          onClick={() => {
            if (onShare) onShare();
          }}
          disabled={shareDisabled}
        >
          Share
        </button>
      </div>
    </div>
  );
};

export default UserSelector;
